import { ClerkProvider, useAuth, useClerk } from "@clerk/clerk-react";
import { esES } from "@clerk/localizations";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, fetchConfig, fetchPublicConfig, getDevUser, setDevUser as persistDevUser, setTokenProvider, signOut, type AppConfig, type AuthMode, type PublicConfig } from "../api";
import { LOCALE_EVENT, LocaleContext, loadLocale, storeLocale, type Locale } from "../i18n";
import { applyBrand, type BrandTokens } from "../theme";
import { Button, ErrorState, LoadingState } from "./ui";

// ---------------------------------------------------------------------------------------------------------------------
// AuthRoot: mounted once above the whole app. Knows the auth mode from the public config and mounts ClerkProvider only
// when the deployment runs Clerk. Passcode and dev modes need nothing here.
// ---------------------------------------------------------------------------------------------------------------------

interface AuthCtx {
  mode: AuthMode;
  clerkPublishableKey: string | null;
  publicAgency: PublicConfig["agency"] | null;
}

const AuthContext = createContext<AuthCtx>({ mode: "dev", clerkPublishableKey: null, publicAgency: null });

export function useAuthMode(): AuthCtx {
  return useContext(AuthContext);
}

/** Clerk's components take the agency brand: primary color, ink, body font and the same radius as our cards. */
function clerkAppearance(b: BrandTokens | undefined) {
  if (!b) return undefined;
  return {
    variables: { colorPrimary: b.primary, colorText: b.ink, colorBackground: "#ffffff", colorInputBackground: "#ffffff", fontFamily: `"${b.fontBody}", system-ui, sans-serif`, borderRadius: "0.9rem" },
    elements: { formButtonPrimary: "font-bold normal-case", card: "shadow-card", footerActionLink: "font-semibold" },
  };
}

/** The stored UI locale, re-read when any screen stores a new one (sign-in page toggle, header toggle). */
function useStoredLocale(fallback: Locale): Locale {
  const [l, setL] = useState<Locale>(() => loadLocale(fallback));
  useEffect(() => {
    const on = () => setL(loadLocale(fallback));
    window.addEventListener(LOCALE_EVENT, on);
    return () => window.removeEventListener(LOCALE_EVENT, on);
  }, [fallback]);
  return l;
}

export function AuthRoot({ children }: { children: ReactNode }) {
  const pub = useQuery({ queryKey: ["publicConfig"], queryFn: fetchPublicConfig, staleTime: 300_000, retry: 2 });
  const navigate = useNavigate();
  const uiLocale = useStoredLocale(pub.data?.agency.defaultLocale ?? "en");
  if (pub.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingState />
      </div>
    );
  }
  const mode: AuthMode = pub.data?.auth.mode ?? "dev";
  const key = pub.data?.auth.clerkPublishableKey ?? null;
  const value: AuthCtx = { mode, clerkPublishableKey: key, publicAgency: pub.data?.agency ?? null };
  if (mode === "clerk" && key) {
    return (
      <AuthContext.Provider value={value}>
        <ClerkProvider publishableKey={key} routerPush={(to) => navigate(to)} routerReplace={(to) => navigate(to, { replace: true })} signInUrl="/sign-in" signUpUrl="/sign-up" afterSignOutUrl="/sign-in" appearance={clerkAppearance(pub.data?.agency.brand)} localization={uiLocale === "es" ? esES : undefined}>
          <ClerkBridge />
          {children}
        </ClerkProvider>
      </AuthContext.Provider>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hands Clerk's getToken / signOut to the plain-function API client. */
function ClerkBridge() {
  const { getToken, isLoaded } = useAuth();
  const clerk = useClerk();
  useEffect(() => {
    if (!isLoaded) return;
    setTokenProvider(() => getToken(), () => clerk.signOut());
    return () => setTokenProvider(null, null);
  }, [isLoaded, getToken, clerk]);
  return null;
}

// ---------------------------------------------------------------------------------------------------------------------
// ConfigProvider: authenticated agency config, locale, dev user switcher, and the first-run redirect to /setup.
// ---------------------------------------------------------------------------------------------------------------------

interface Ctx {
  config: AppConfig;
  devUser: string;
  switchUser: (id: string) => void;
  authMode: AuthMode;
  setup: AppConfig["setup"];
}

const ConfigContext = createContext<Ctx | null>(null);

export function useConfig(): Ctx {
  const c = useContext(ConfigContext);
  if (!c) throw new Error("useConfig outside ConfigProvider");
  return c;
}

export function ConfigProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const auth = useAuthMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [devUser, setDevUserState] = useState(getDevUser());
  const q = useQuery({ queryKey: ["config", devUser], queryFn: fetchConfig, staleTime: 60_000, retry: (count, err) => !(err instanceof ApiError && (err.status === 401 || err.status === 403)) && count < 1 });
  const [locale, setLocaleState] = useState<Locale>(() => loadLocale("en"));
  const [localeTouched, setLocaleTouched] = useState(false);

  useEffect(() => {
    if (q.data) {
      applyBrand(q.data.agency.brand);
      document.title = `${q.data.agency.displayName} · Workspace`;
      if (!localeTouched && !localStorageHasLocale()) setLocaleState(q.data.user.locale);
    }
  }, [q.data, localeTouched]);

  // First run: until setup is completed or explicitly skipped, every path lands on the wizard.
  useEffect(() => {
    if (!q.data) return;
    const s = q.data.setup;
    if (!s.completed && !s.skipped && location.pathname !== "/setup") navigate("/setup", { replace: true });
  }, [q.data, location.pathname, navigate]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleTouched(true);
    setLocaleState(l);
    storeLocale(l);
    document.documentElement.lang = l;
  }, []);

  const switchUser = useCallback(
    (id: string) => {
      persistDevUser(id);
      setDevUserState(id);
      qc.clear();
    },
    [qc],
  );

  const localeValue = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);

  if (q.isLoading) {
    return (
      <LocaleContext.Provider value={localeValue}>
        <div className="min-h-screen flex items-center justify-center">
          <LoadingState />
        </div>
      </LocaleContext.Provider>
    );
  }
  if (q.isError || !q.data) {
    const err = q.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 401) {
      // api.ts already redirected; render nothing meaningful while the browser navigates.
      return (
        <LocaleContext.Provider value={localeValue}>
          <div className="min-h-screen flex items-center justify-center"><LoadingState label="Redirecting to sign-in…" /></div>
        </LocaleContext.Provider>
      );
    }
    return (
      <LocaleContext.Provider value={localeValue}>
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md w-full">
            <ErrorState error={err ?? "No config"} onRetry={() => q.refetch()} />
            {status === 403 ? (
              <div className="mt-3 flex items-center gap-3">
                <p className="text-sm text-ink/60 flex-1">You are signed in, but this agency has no staff record for you.</p>
                <Button variant="outline" size="sm" onClick={() => void signOut()}>Sign out</Button>
              </div>
            ) : auth.mode === "dev" ? (
              <p className="mt-3 text-sm text-ink/60">Is the API running on port 8787?</p>
            ) : (
              <div className="mt-3 flex justify-end"><Button variant="outline" size="sm" onClick={() => void signOut()}>Sign out</Button></div>
            )}
          </div>
        </div>
      </LocaleContext.Provider>
    );
  }

  const value: Ctx = { config: q.data, devUser, switchUser, authMode: q.data.authMode, setup: q.data.setup };
  return (
    <LocaleContext.Provider value={localeValue}>
      <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
    </LocaleContext.Provider>
  );
}

function localStorageHasLocale() {
  try {
    return localStorage.getItem("agency.locale") != null;
  } catch {
    return false;
  }
}
